import React, { createContext, useContext, useMemo } from 'react';

/**
 * Which product the Real Time pages are being shown as. Telcofy Emergency reuses the Real Time
 * pages whole, so a page asks the scope where its links go (productPath) and what it is called
 * (scopeTitle) instead of hard-coding '/realtime' or "Realtime". Default: Real Time, so the
 * existing routes behave exactly as before.
 */
export type ScopeProduct = 'realtime' | 'emergency';

export interface ProductScope {
  product: ScopeProduct;
  /** Real Time: '/realtime' (its landing is '/monitor-areas-select'). Emergency: '/emergency'. */
  basePath: string;
}

export type ScopePage = 'landing' | 'dashboard' | 'project' | 'report';

export const REALTIME_SCOPE: ProductScope = { product: 'realtime', basePath: '/realtime' };
export const EMERGENCY_SCOPE: ProductScope = { product: 'emergency', basePath: '/emergency' };

const REALTIME_LANDING = '/monitor-areas-select';
const EMERGENCY_TITLE = 'Telcofy Emergency';

/** The path of a page inside a scope, without query string. */
export function productPath(scope: ProductScope, page: ScopePage): string {
  const { product, basePath } = scope;
  if (page === 'landing') return product === 'emergency' ? basePath : REALTIME_LANDING;
  if (page === 'dashboard') return product === 'emergency' ? `${basePath}/dashboard` : basePath;
  return `${basePath}/${page}`;
}

/** The product's name as a page calls it: "Telcofy Emergency", or the Real Time wording the page gives. */
export function scopeName(scope: ProductScope, realtimeName: string): string {
  return scope.product === 'emergency' ? EMERGENCY_TITLE : realtimeName;
}

/**
 * The title a page shows. Real Time keeps the title the page gives; Emergency is always
 * "Telcofy Emergency", with `emergencySuffix` after a middle dot where the page says more.
 */
export function scopeTitle(scope: ProductScope, realtimeTitle: string, emergencySuffix?: string): string {
  if (scope.product !== 'emergency') return realtimeTitle;
  return emergencySuffix ? `${scopeName(scope, realtimeTitle)} · ${emergencySuffix}` : scopeName(scope, realtimeTitle);
}

/** Prefix for export filenames: Real Time keeps the prefix the page gives; Emergency is "telcofy-emergency". */
export function scopeFilePrefix(scope: ProductScope, realtimePrefix: string): string {
  return scope.product === 'emergency' ? 'telcofy-emergency' : realtimePrefix;
}

const Ctx = createContext<ProductScope>(REALTIME_SCOPE);

export const ProductScopeProvider: React.FC<{
  /** Either the whole scope, or just the product (its standard base path is used). */
  value?: ProductScope;
  product?: ScopeProduct;
  children: React.ReactNode;
}> = ({ value, product, children }) => {
  const scope = useMemo<ProductScope>(
    () => value ?? (product === 'emergency' ? EMERGENCY_SCOPE : REALTIME_SCOPE),
    [value, product],
  );
  return <Ctx.Provider value={scope}>{children}</Ctx.Provider>;
};

export const useProductScope = (): ProductScope => useContext(Ctx);
